import { BREAKPOINTS } from '@/core/breakpointsController'
import { swap } from '@/core/gsap'

import BaseComponent from '@/components/base'
import ProductCard from '@/components/product/productCard'
import A11yStatus from '@/components/a11y/a11yStatus'

const selectors = {
  list: 'ul',
  more: 'a[data-more]'
}

interface ResultsDisplaySettings {
  onMoreIntersection?: (entries: IntersectionObserverEntry[]) => void
  onReplaceStart?: (resultsDisplay: ResultsDisplay) => void
  onReplaceComplete?: (resultsDisplay: ResultsDisplay) => void
}

export default class ResultsDisplay extends BaseComponent {
  static TYPE = 'results-display'

  #swapTl: gsap.core.Timeline | null
  #moreObserver: IntersectionObserver | null

  settings: ResultsDisplaySettings
  productCards: ProductCard[]
  list: HTMLUListElement | null
  a11yStatus: A11yStatus | null
  more: HTMLAnchorElement | undefined

  constructor(el: HTMLElement, options: ResultsDisplaySettings = {}) {
    super(el)

    this.settings = {
      ...options
    }

    this.#swapTl = null
    this.#moreObserver = null
    this.productCards = []
    this.list = null
    this.a11yStatus = null
    this.more = null


    this.onMoreIntersection = this.onMoreIntersection.bind(this)

    this.setup()
  }

  observeMore() {
    if (!this.more || !this.#moreObserver) return

    this.#moreObserver.unobserve(this.more)

    requestAnimationFrame(() => {
      if (!this.more || !this.#moreObserver) return

      this.#moreObserver.observe(this.more)
      this.requestMoreIfNeeded()
    })
  }

  requestMoreIfNeeded() {
    if (!this.more) return

    const doc = document.documentElement
    const underfilled = doc.scrollHeight <= (window.innerHeight + 120)
    const moreRect = this.more.getBoundingClientRect()
    const sentinelNearViewport = moreRect.top <= (window.innerHeight + 200)

    if (!underfilled && !sentinelNearViewport) return

    // Build a minimal "intersecting" shape to reuse existing fetch flow.
    this.settings.onMoreIntersection?.([
      {
        isIntersecting: true,
        target: this.more
      } as IntersectionObserverEntry
    ])
  }

  setup() {
    this.productCards = this.qsa(ProductCard.SELECTOR).map(el => new ProductCard(el))

    this.list = this.qs(selectors.list) as HTMLUListElement | null
    this.more = this.qs(selectors.more) as HTMLAnchorElement | null

    if (this.more) {
      // Trigger slightly before reaching the sentinel so compact/mobile layouts don't require deep scroll.
      const rootMargin = window.innerWidth < BREAKPOINTS.md ? '0px 0px 40% 0px' : '0px 0px 30% 0px'

      this.#moreObserver = new IntersectionObserver(this.onMoreIntersection, {
        root: null,
        rootMargin,
        threshold: 0
      })

      this.observeMore()
    }

    this.a11yStatus = A11yStatus.generate(this.el)  
  }

  teardown() {
    this.productCards.forEach(card => card.destroy())
    this.productCards = []

    this.list = null
    this.more = null

    this.#moreObserver?.disconnect()
    this.#moreObserver = null

    this.a11yStatus?.el?.remove()
    this.a11yStatus?.destroy()
    this.a11yStatus = null
  }

  destroy() {
    this.#swapTl?.kill()
    this.#swapTl = null

    this.teardown()

    super.destroy()
  }

  // Replace the entire contents of the results display
  replace(dom: HTMLElement | undefined) {
    if (!this.validateDom(dom)) return

    this.#swapTl?.kill()
    this.#swapTl = swap(this.el, {
      onExitStart: () => {
        this.settings.onReplaceStart?.(this)
      },
      onExitComplete: () => {
        this.teardown()
        this.el.innerHTML = dom.innerHTML
        this.setup()

        this.a11yStatus.text = 'Results updated'

        this.settings.onReplaceComplete?.(this)
      }
    })
  }

  add(dom: HTMLElement | undefined) {
    if (!this.validateDom(dom)) return

    const newList = dom.querySelector(selectors.list)
    const newItems = newList ? [...newList.children] : []

    // Add all items to the list
    if (newItems.length) {
      const fragment = document.createDocumentFragment()

      newItems.forEach(el => {      
        fragment.append(el) // el === <li>
        
        const card = el.querySelector(ProductCard.SELECTOR) as HTMLElement

        if (card) {
          this.productCards.push(new ProductCard(card))
        }
      })

      this.list.append(fragment)
      this.a11yStatus.text = `${newItems.length} items loaded`

      // Replace the "more" link if it exists
      const newMore = dom.querySelector(selectors.more) as HTMLAnchorElement | undefined

      if (this.more && newMore) {
        this.more.href = newMore.href
        this.observeMore()
      }
      else {
        this.onNoMoreResults()
      }
    }    
  }

  onNoMoreResults() {
    this.more.remove()
    this.more = null

    this.#moreObserver?.disconnect()
    this.#moreObserver = null
  }  

  onMoreIntersection(entries: IntersectionObserverEntry[]) {
    if (!this.more) return // Prevent a race condition

    this.settings.onMoreIntersection?.(entries)
  }
}